import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { NavigationGridItemStyles } from "@SupplyLand/Components/NavigationGridItem";
import CategorySpotlightPanel from "@SupplyLand/Widgets/Category/CategorySpotlightPanel";
import * as cssLinter from "css";
import * as React from "react";
import { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showTitle = "showTitle",
    customTitle = "customTitle",
    useAllCategories = "useAllCategories",
    selectedCategoryIds = "selectedCategoryIds",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps, HasCategoryContext {
    extendedStyles?: CategorySpotlightStyles;
    fields: {
        [fields.showTitle]: boolean;
        [fields.customTitle]: string;
        [fields.useAllCategories]: boolean;
        [fields.selectedCategoryIds]: string[];
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const getCategoriesParameter = {
        maxDepth: 2,
        includeStartCategory: false,
    };
    return {
        categoriesDataView: getCategoriesDataView(state, getCategoriesParameter),
        getCategoriesParameter,
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CategorySpotlightStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    subCategoryItem?: NavigationGridItemStyles;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
}

export const categorySpotlightStyles: CategorySpotlightStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: {
        gap: 20,
    },
    subCategoryItem: {
        childPageItem: {
            css: css`
                display: inline-block;
                max-width: none;
                flex-basis: initial;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            width: 33%;
                        `,
                        css`
                            width: 33%;
                        `,
                        css`
                            width: 33%;
                        `,
                        css`
                            width: 25%;
                        `,
                        css`
                            width: 20%;
                        `,
                    ]);
                }}
            `,
        },
    },
    titleWrapper: {
        css: css`
            padding: 10px;
            text-align: center;
            margin-bottom: 5px;
        `,
    },
    title: {
        size: 24,
        weight: 600,
    },
};

const defaultCustomCss = `.spotlight-wrapper {

}

.spotlight-title-wrapper {

}

.spotlight-title {

}
`;

const CategorySpotlight: FC<Props> = props => {
    const { categoriesDataView, getCategoriesParameter, topLevelCategoryIds, fields, extendedStyles } = props;
    const { useAllCategories, selectedCategoryIds, showTitle, customTitle } = fields;

    const [styles] = React.useState(() => mergeToNew(categorySpotlightStyles, extendedStyles));

    useEffect(() => {
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories(getCategoriesParameter);
        }
    }, []);

    if (
        (useAllCategories && !categoriesDataView.value) ||
        (useAllCategories && categoriesDataView.value && categoriesDataView.value.length === 0)
    ) {
        return null;
    }

    if (
        (!useAllCategories && !selectedCategoryIds) ||
        (!useAllCategories && selectedCategoryIds && selectedCategoryIds.length === 0)
    ) {
        return null;
    }

    // Return this when categoriesDataView is loading
    if (categoriesDataView.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    let categories = categoriesDataView.value;
    let firstCategories;
    let secondCategories;

    if (topLevelCategoryIds && categories) {
        const topLevelCategories = categories.filter(c => topLevelCategoryIds.indexOf(c.id) > -1);
        const restrictedCategoryNames = ["Free Swag"];
        const restrictedCategories = topLevelCategories.filter(c => restrictedCategoryNames.indexOf(c.name) > -1);
        const restrictedCategorySubcategoryIds: string[] = [];

        restrictedCategories.forEach(rc => {
            if (rc.subCategoryIds) {
                restrictedCategorySubcategoryIds.push(...rc.subCategoryIds);
            }
        });

        // Strip out top level categories
        categories = categories.filter(o => topLevelCategoryIds.indexOf(o.id) === -1);
        // Strip out restricted category subscategory ids
        categories = categories.filter(o => restrictedCategorySubcategoryIds.indexOf(o.id) === -1);
        categories = categories?.slice().sort((a, b) => a.name.localeCompare(b.name));

        firstCategories = categories.slice(0, 30);
        secondCategories = categories.slice(30, categories.length);
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    // Return this when useAllCategories === true.
    if (useAllCategories && firstCategories && secondCategories) {
        return (
            <StyledWrapper {...customCssWrapper}>
                <StyledWrapper {...styles.wrapper} className="spotlight-wrapper">
                    {showTitle && (
                        <StyledWrapper {...styles.titleWrapper} className="spotlight-title-wrapper">
                            <Typography {...styles.title} className="spotlight-title">
                                {customTitle}
                            </Typography>
                        </StyledWrapper>
                    )}
                    <GridContainer {...styles.container}>
                        {firstCategories.map(category => (
                            <CategorySpotlightPanel
                                key={category.id}
                                categoryId={category.id}
                                childStyles={styles.subCategoryItem}
                            />
                        ))}
                    </GridContainer>
                    <Zone zoneName="MiddleContent" contentId={props.id} />
                    <GridContainer {...styles.container}>
                        {secondCategories.map(category => (
                            <CategorySpotlightPanel
                                key={category.id}
                                categoryId={category.id}
                                childStyles={styles.subCategoryItem}
                            />
                        ))}
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    // Return this when useAllCategories === false.
    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper {...styles.wrapper} className="spotlight-wrapper">
                {showTitle && (
                    <StyledWrapper {...styles.titleWrapper} className="spotlight-title-wrapper">
                        <Typography {...styles.title} className="spotlight-title">
                            {customTitle}
                        </Typography>
                    </StyledWrapper>
                )}
                <GridContainer {...styles.container}>
                    {selectedCategoryIds.map(categoryId => (
                        <CategorySpotlightPanel
                            key={categoryId}
                            categoryId={categoryId}
                            childStyles={styles.subCategoryItem}
                        />
                    ))}
                    <Zone zoneName="MiddleContent" contentId={props.id} />
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const generalTab = {
    displayName: "Basic",
    sortOrder: 1,
};

const categoriesTab = {
    displayName: "Categories",
    sortOrder: 2,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 3,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCategory(CategorySpotlight)),
    definition: {
        group: "Categories",
        displayName: "Category Spotlight",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.useAllCategories,
                displayName: "Display All Categories",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tooltip: "Selecting this option will override any Selected Categories.",
                tab: generalTab,
                sortOrder: 10,
            },
            {
                name: fields.showTitle,
                displayName: "Show Title",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: generalTab,
                sortOrder: 20,
            },
            {
                name: fields.customTitle,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Category Spotlight",
                fieldType: "General",
                isRequired: true,
                isVisible: item => item.fields.showTitle === true,
                tab: generalTab,
                sortOrder: 30,
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Selected Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
                tab: categoriesTab,
                sortOrder: 100,
                isRequired: true,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                sortOrder: 200,
                defaultValue: defaultCustomCss,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
